import { IMAGES } from './site';

// Services actually offered by Nebraska Backyard Living (Lee Contracting LLC),
// extracted from the existing website. Do not add services the company does not provide.
export const SERVICES = [
  {
    slug: 'outdoor-kitchens',
    title: 'Custom Outdoor Kitchens',
    navTitle: 'Outdoor Kitchens',
    category: 'Outdoor Living',
    excerpt: 'Professional-grade outdoor cooking spaces with custom cabinetry, premium appliances, and weather-resistant materials.',
    image: IMAGES.kitchen,
    metaTitle: 'Outdoor Kitchens Omaha, NE | Nebraska Backyard Living',
    metaDescription: 'Custom outdoor kitchens designed & built for Omaha and Lincoln homes — premium appliances, weather-proof materials, transparent pricing. Free consultation: (402) 202-9111.',
    intro: [
      'A well-designed outdoor kitchen changes how you use your entire backyard. Instead of running between the grill and the house, everything you need — cooking, prep, storage, and seating — lives in one beautifully built space.',
      'Nebraska Backyard Living designs and builds custom outdoor kitchens for homeowners across the Omaha and Lincoln metro areas, engineered with weather-resistant materials that stand up to Nebraska heat, storms, and freeze-thaw winters.',
    ],
    benefits: [
      'Cook and entertain without leaving your guests',
      'Premium, weather-resistant cabinetry and appliances',
      'Layouts designed around how you actually cook and host',
      'Adds functional living space and lasting home value',
    ],
    options: {
      title: 'Design Options',
      items: ['Built-in grills and side burners', 'Outdoor refrigeration and ice makers', 'Stone, brick, and stucco island finishes', 'Granite and concrete countertops', 'Bar seating and dining integration', 'Pergola or patio cover pairing for shade'],
    },
    faqs: [
      { q: 'How much does an outdoor kitchen cost in Nebraska?', a: 'Every outdoor kitchen we build is custom, so pricing depends on size, appliances, and finishes. We provide transparent, itemized pricing after your free on-site consultation — no surprises.' },
      { q: 'Can an outdoor kitchen handle Nebraska winters?', a: 'Yes — when it is built correctly. We use weather-resistant materials and construction methods chosen specifically for Nebraska\'s freeze-thaw cycles, and we will walk you through simple seasonal care.' },
      { q: 'Do you handle the design, or do I need my own plans?', a: 'We handle everything. Our custom design-build process takes you from initial ideas to finished kitchen with one accountable team.' },
    ],
    related: ['patios', 'patio-covers', 'fire-features'],
  },
  {
    slug: 'sunrooms',
    title: 'Sunroom Design & Installation',
    navTitle: 'Sunrooms',
    category: 'Outdoor Living',
    excerpt: 'Beautiful enclosed spaces that bring the outdoors in, perfect for year-round relaxation and entertainment.',
    image: IMAGES.sunroom,
    metaTitle: 'Sunroom Design & Installation Omaha, NE | Nebraska Backyard Living',
    metaDescription: 'Custom sunrooms built for Nebraska seasons — year-round light and comfort for Omaha & Lincoln homes. 22+ years experience. Schedule a free consultation.',
    intro: [
      'A sunroom gives you the light, views, and feeling of being outdoors — with none of the wind, insects, or unpredictable Nebraska weather. It is one of the most versatile spaces you can add to your home.',
      'We specialize in designing and constructing custom sunrooms that blend seamlessly with your home\'s architecture, built for genuine year-round comfort in the Omaha and Lincoln climate.',
    ],
    benefits: [
      'Enjoy your backyard views in every season',
      'Flooded with natural light, protected from bugs and weather',
      'Designed to match your home\'s existing architecture',
      'A flexible space for relaxing, dining, or entertaining',
    ],
    options: {
      title: 'Sunroom Options',
      items: ['Three-season and four-season designs', 'Floor-to-ceiling window walls', 'Insulated roof systems', 'Heating and cooling integration', 'Matching exterior finishes and trim', 'Direct patio and deck connections'],
    },
    faqs: [
      { q: 'What is the difference between a three-season and four-season sunroom?', a: 'A three-season room is designed for spring through fall comfort, while a four-season sunroom is fully insulated and climate-controlled for year-round use. We will help you decide which fits how you want to live.' },
      { q: 'Will a sunroom look like an add-on?', a: 'Not when it is designed correctly. We match rooflines, finishes, and proportions so your sunroom feels like it was always part of your home.' },
      { q: 'Do sunrooms require permits in Omaha or Lincoln?', a: 'Most sunroom projects do require permits. With 22+ years of local experience, we are deeply familiar with local building requirements and handle the details for you.' },
    ],
    related: ['patio-enclosures', 'custom-outdoor-design', 'patios'],
  },
  {
    slug: 'patio-enclosures',
    title: 'Custom Patio Enclosures',
    navTitle: 'Patio Enclosures',
    category: 'Outdoor Living',
    excerpt: 'Transform your open patio into a versatile, protected outdoor room you can use far more of the year.',
    image: IMAGES.enclosure,
    metaTitle: 'Patio Enclosures Omaha, NE | Nebraska Backyard Living',
    metaDescription: 'Turn your patio into a protected outdoor room. Custom screened & glass patio enclosures across Omaha & Lincoln, NE. Free on-site consultation.',
    intro: [
      'If you already have a patio you love but can only use a few months a year, a custom patio enclosure changes everything. Screens, glass, and roof systems turn open concrete into a comfortable outdoor room.',
      'We design patio enclosures that protect against Nebraska wind, rain, and insects while keeping the open, airy feeling that made you love the space in the first place.',
    ],
    benefits: [
      'Use your patio more months of the year',
      'Protection from insects, wind, and rain',
      'Keeps the open-air feel with screened or glassed options',
      'A cost-effective way to add functional living space',
    ],
    options: {
      title: 'Enclosure Options',
      items: ['Screened patio rooms', 'Glass and window wall systems', 'Insulated and open roof structures', 'Ceiling fans and lighting integration', 'Pet-resistant screening', 'Heating options for shoulder seasons'],
    },
    faqs: [
      { q: 'Can you enclose my existing patio?', a: 'In most cases, yes. We start with an on-site assessment of your existing slab, roofline, and structure, then design an enclosure that works with what you have.' },
      { q: 'Screened vs. glass — which should I choose?', a: 'Screened rooms maximize airflow and the outdoor feel; glass extends your usable season. Many homeowners combine both. We will walk you through the trade-offs at your consultation.' },
    ],
    related: ['sunrooms', 'patio-covers', 'patios'],
  },
  {
    slug: 'decks',
    title: 'Deck Building & Renovation',
    navTitle: 'Decks',
    category: 'Outdoor Living',
    excerpt: 'High-quality custom decks for relaxation and entertainment, built to handle Nebraska weather.',
    image: IMAGES.deck,
    metaTitle: 'Deck Building & Renovation Omaha, NE | Nebraska Backyard Living',
    metaDescription: 'Custom decks built for Nebraska weather — new builds and renovations across Omaha & Lincoln. Locally owned, A+ BBB. Get a free quote today.',
    intro: [
      'A great deck extends your home into the outdoors — morning coffee, weeknight dinners, weekend gatherings. A poorly built one becomes a maintenance burden. The difference is design, materials, and craftsmanship.',
      'Nebraska Backyard Living builds and renovates custom decks across the Omaha and Lincoln metro, with construction methods proven over 22+ years of Nebraska seasons.',
    ],
    benefits: [
      'Custom layouts shaped to your yard and home',
      'Material guidance for Nebraska\'s climate',
      'Solid, code-conscious construction',
      'Renovation options for aging decks',
    ],
    options: {
      title: 'Deck Options',
      items: ['Pressure-treated and cedar framing', 'Low-maintenance composite decking', 'Multi-level and wraparound layouts', 'Built-in benches and planters', 'Pergola and privacy screen integration', 'Railing systems in wood, metal, and composite'],
    },
    faqs: [
      { q: 'Wood or composite decking — which is better for Nebraska?', a: 'Both perform well when installed correctly. Composite minimizes maintenance; wood offers a natural look at a lower upfront cost. We will help you weigh lifespan, upkeep, and budget honestly.' },
      { q: 'Can you rebuild on my existing deck frame?', a: 'Sometimes. We inspect the existing structure first — if the framing is sound, a re-surface can be a smart, budget-friendly renovation. If it is not, we will tell you straight.' },
    ],
    related: ['pergolas', 'patios', 'custom-outdoor-design'],
  },
  {
    slug: 'pergolas',
    title: 'Pergolas',
    navTitle: 'Pergolas',
    category: 'Outdoor Living',
    excerpt: 'Custom pergolas that add shade, structure, and architectural presence to your outdoor space.',
    image: IMAGES.pergola,
    metaTitle: 'Pergolas Omaha, NE | Custom Pergola Builder | Nebraska Backyard Living',
    metaDescription: 'Custom pergolas that add shade, structure & style to Omaha and Lincoln backyards. Designed on-site, built to last. Free consultation: (402) 202-9111.',
    intro: [
      'A pergola does more than provide shade — it defines a space. Over a patio, a dining area, or a hot tub, a well-proportioned pergola turns an open yard into an outdoor room with architectural presence.',
      'We design and build custom pergolas sized and styled for your home, from warm cedar structures to clean modern profiles, built to stay beautiful through Nebraska wind and weather.',
    ],
    benefits: [
      'Defines outdoor dining and lounge areas',
      'Filtered shade without closing in the space',
      'Strong architectural focal point',
      'Pairs beautifully with lighting, fans, and climbing plants',
    ],
    options: {
      title: 'Pergola Options',
      items: ['Cedar and pressure-treated wood', 'Modern aluminum and steel profiles', 'Adjustable louvered roof systems', 'Retractable shade canopies', 'Integrated lighting and ceiling fans', 'Privacy screens and decorative end cuts'],
    },
    faqs: [
      { q: 'Pergola or covered patio — how do I choose?', a: 'A pergola offers filtered shade and an open-air feel; a covered patio gives full weather protection. We design both and will recommend what fits your space, sun exposure, and budget.' },
      { q: 'How big should my pergola be?', a: 'It should be scaled to what it covers — a dining set, a seating group, a grill station. We design proportions on-site so the structure feels intentional, not oversized or lost.' },
    ],
    related: ['decks', 'patio-covers', 'patios'],
  },
  {
    slug: 'water-features',
    title: 'Water Features',
    navTitle: 'Water Features',
    category: 'Water & Fire',
    excerpt: 'Serene fountains, ponds, and water elements that add tranquility and movement to your landscape.',
    image: IMAGES.waterFeature,
    metaTitle: 'Water Features Omaha, NE | Nebraska Backyard Living',
    metaDescription: 'Serene custom water features — fountains, pondless waterfalls & streams for Omaha & Lincoln landscapes. Designed for easy care. Free consultation.',
    intro: [
      'Moving water changes the character of a backyard. The sound softens street noise, draws birds, and gives the eye a place to rest — turning a pleasant yard into a genuinely tranquil retreat.',
      'From sculptural fountains to naturalistic ponds and waterfalls, we design and build water features scaled to your space and engineered for straightforward maintenance.',
    ],
    benefits: [
      'Calming sound that masks street and neighbor noise',
      'A living focal point for your landscape',
      'Attracts birds and pollinators',
      'Designed for manageable, seasonal care',
    ],
    options: {
      title: 'Water Feature Options',
      items: ['Bubbling rock and urn fountains', 'Pondless waterfalls', 'Ornamental ponds with aquatic plantings', 'Stream and cascade features', 'Integrated underwater lighting', 'Formal basins for patio settings'],
    },
    faqs: [
      { q: 'Are water features high maintenance?', a: 'They do not have to be. Pondless waterfalls and fountain systems are notably low-maintenance. We design every feature with realistic upkeep in mind and show you exactly what care looks like.' },
      { q: 'What happens to a water feature in a Nebraska winter?', a: 'Most features are winterized in the fall — a straightforward process we walk you through. We build with freeze-thaw conditions in mind from the start.' },
    ],
    related: ['fire-features', 'patios', 'custom-outdoor-design'],
  },
  {
    slug: 'fire-features',
    title: 'Fire Pits & Outdoor Fireplaces',
    navTitle: 'Fire Features',
    category: 'Water & Fire',
    excerpt: 'Custom fire pits and outdoor fireplaces that create warmth, ambiance, and a natural gathering place.',
    image: IMAGES.firePit,
    metaTitle: 'Fire Pits & Outdoor Fireplaces Omaha, NE | Nebraska Backyard Living',
    metaDescription: 'Custom fire pits & outdoor fireplaces that extend your Nebraska outdoor season. Gas & wood designs across Omaha & Lincoln. Get a free quote.',
    intro: [
      'A fire feature is the fastest way to make a backyard irresistible. It extends your outdoor season on both ends, draws people together, and gives your patio a natural heart.',
      'We design and build custom fire pits and outdoor fireplaces — wood-burning and gas — sized, sited, and finished to complement your patio and home.',
    ],
    benefits: [
      'Extends your outdoor season into spring and fall',
      'A natural gathering point for family and friends',
      'Wood-burning authenticity or gas convenience',
      'Built to be safe, durable, and beautiful',
    ],
    options: {
      title: 'Fire Feature Options',
      items: ['Built-in stone and paver fire pits', 'Gas fire pits with push-button ignition', 'Full outdoor fireplaces', 'Fire tables and linear burners', 'Seat walls and surrounding patios', 'Lava rock, fire glass, and log-set finishes'],
    },
    faqs: [
      { q: 'Wood or gas fire pit — which should I pick?', a: 'Wood gives you the crackle and ritual; gas gives you instant, smokeless convenience. Many Omaha-area homeowners choose gas for weeknight use. We build both and will help you decide.' },
      { q: 'Where should a fire pit go in my yard?', a: 'Siting matters — wind patterns, distance from structures, sightlines from the house, and how people will gather around it. We plan placement during your design consultation.' },
    ],
    related: ['patios', 'outdoor-kitchens', 'water-features'],
  },
  {
    slug: 'patios',
    title: 'Patio Design & Installation',
    navTitle: 'Patios',
    category: 'Outdoor Living',
    excerpt: 'Beautiful, durable patio surfaces that extend your living space and anchor your entire backyard.',
    image: IMAGES.patio,
    metaTitle: 'Patio Installation Omaha, NE | Paver Patios | Nebraska Backyard Living',
    metaDescription: 'Professional patio installation in Omaha & Lincoln, NE — pavers, natural stone & concrete with freeze-thaw-proof base work. Free on-site consultation.',
    intro: [
      'The patio is the foundation of outdoor living. It is where the kitchen lands, where the fire pit gathers people, where furniture sits level and mornings start with coffee outside.',
      'We design and install custom patios across the Omaha and Lincoln metro — proper base preparation, quality materials, and layouts designed around how you want to live outdoors.',
    ],
    benefits: [
      'A solid foundation for every other outdoor feature',
      'Layouts designed around entertaining and daily life',
      'Proper base work for Nebraska freeze-thaw cycles',
      'Materials chosen for durability and timeless style',
    ],
    options: {
      title: 'Patio Materials & Options',
      items: ['Concrete pavers in modern and classic styles', 'Natural stone and flagstone', 'Poured and stamped concrete', 'Seat walls and raised planters', 'Integrated steps and landing transitions', 'Fire pit and kitchen-ready layouts'],
    },
    faqs: [
      { q: 'How much does a patio cost in Omaha?', a: 'Patio pricing depends on size, material, site conditions, and features like seat walls or steps. After a free on-site consultation, we provide clear, itemized pricing — no pressure, no surprises.' },
      { q: 'Pavers or stamped concrete?', a: 'Pavers offer superior repairability and freeze-thaw flexibility; stamped concrete can be cost-effective for larger areas. We install both and will recommend what suits your site and budget.' },
      { q: 'How long does a patio installation take?', a: 'Most residential patios are completed in days rather than weeks, depending on size and complexity. We give you a realistic timeline before work begins and keep you updated throughout.' },
    ],
    related: ['patio-covers', 'fire-features', 'outdoor-kitchens'],
  },
  {
    slug: 'patio-covers',
    title: 'Patio Covers',
    navTitle: 'Patio Covers',
    category: 'Outdoor Living',
    excerpt: 'Protective covers that extend your living space and provide year-round outdoor comfort.',
    image: IMAGES.pergolaModern,
    metaTitle: 'Patio Covers Omaha, NE | Nebraska Backyard Living',
    metaDescription: 'Custom patio covers for shade, shelter & year-round comfort across Omaha & Lincoln. Engineered for Nebraska wind & snow. Free consultation.',
    intro: [
      'Nebraska sun and sudden storms can make an uncovered patio unusable exactly when you want it most. A well-designed patio cover changes that — shade in July, shelter in a passing shower, and a space that feels like a true outdoor room.',
      'We build custom patio covers that tie cleanly into your home\'s roofline and architecture, engineered for Nebraska wind and snow loads.',
    ],
    benefits: [
      'Shade and shelter that extend your outdoor season',
      'Protects furniture, kitchens, and finishes',
      'Designed to look like part of the original home',
      'Engineered for Nebraska wind and snow',
    ],
    options: {
      title: 'Patio Cover Options',
      items: ['Solid roof structures tied into the home', 'Insulated roof panel systems', 'Open-beam and hybrid designs', 'Integrated lighting, fans, and heaters', 'Guttering and drainage management', 'Screen or glass wall-ready framing'],
    },
    faqs: [
      { q: 'Patio cover or pergola — what is the difference?', a: 'A patio cover has a solid roof for full weather protection; a pergola offers filtered shade with an open feel. We design and build both, and can help you choose during your consultation.' },
      { q: 'Can a patio cover be added to my existing patio?', a: 'Usually, yes. We assess your existing slab, footings, and roofline on-site, then design a cover that integrates structurally and visually.' },
    ],
    related: ['pergolas', 'patio-enclosures', 'patios'],
  },
  {
    slug: 'custom-outdoor-design',
    title: 'Custom Outdoor Living Design',
    navTitle: 'Custom Design',
    category: 'Design',
    excerpt: 'A true custom design-build approach — one accountable team from first sketch to final walkthrough.',
    image: IMAGES.homeModern,
    metaTitle: 'Backyard Design Omaha, NE | Custom Outdoor Living Design',
    metaDescription: 'Custom backyard design & build in Omaha & Lincoln — one plan, one accountable team, from first sketch to final walkthrough. Free consultation.',
    intro: [
      'The best outdoor spaces are not collections of features — they are compositions. Kitchen, patio, cover, fire, planting, and light working together as one design, built by one accountable team.',
      'That is the Nebraska Backyard Living approach: a true custom design-build process, refined over 22+ years, that takes you from first ideas to a finished outdoor living space tailored to your property, your style, and your budget.',
    ],
    benefits: [
      'One accountable team — design through construction',
      'A cohesive plan instead of piecemeal projects',
      'Transparent, honest pricing before work begins',
      'Designs shaped by 22+ years of local experience',
    ],
    options: {
      title: 'What a Complete Design Can Include',
      items: ['Outdoor kitchens and dining areas', 'Patios, decks, and pergolas', 'Sunrooms and patio enclosures', 'Water and fire features', 'Lighting and entertainment integration', 'Phased plans that grow with your budget'],
    },
    faqs: [
      { q: 'What does the design process look like?', a: 'It starts with a free on-site consultation where we listen first. From there we develop a custom design with transparent pricing, refine it with you, and then build it with our own experienced team.' },
      { q: 'Can I build my project in phases?', a: 'Absolutely — and designing the whole vision first makes phasing smarter. We can master-plan your space so each phase builds toward the complete backyard without rework.' },
    ],
    related: ['outdoor-kitchens', 'sunrooms', 'patios'],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
